import { faBug } from "@fortawesome/pro-regular-svg-icons"
import { faBan, faCircleInfo } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
  alpha
} from "@mui/material"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { getFormattedFileSize } from "app/tools/file"
import { GEO_TARGETS_TYPES, IForm, IGeoTargetsFileImport } from "form/model"
import { getForm } from "form/tools/common"
import {
  getGeoTargetsFileImport,
  importGeoTargetsFile,
  setGeoTargetsFileImportType
} from "form/tools/geo-targets-file-import"
import React from "react"
import { connect } from "react-redux"
import { FlexBox } from "ui/components/FlexBox"
import { GeoTargetsImportFileUpload } from "./GeoTargetsImportFileUpload"

interface IGeoTargetsImportPopupContentProps {
  form: IForm
  fileImport: IGeoTargetsFileImport
}

const GeoTargetsImportPopupContentFn = React.memo(
  ({
    form,
    fileImport: { files, isParsing, parsingError, isSaving, importType, geoTargetsType, rows, error }
  }: IGeoTargetsImportPopupContentProps): JSX.Element => {
    let hasData: boolean
    switch (geoTargetsType) {
      case GEO_TARGETS_TYPES.COORDINATE: {
        hasData = form.geoTargets.coordinates.length !== 0
        break
      }
      case GEO_TARGETS_TYPES.POSTAL_CODE: {
        hasData = form.geoTargets.postalCodes.length !== 0
        break
      }
      default: {
        return <Typography>{`Type ${geoTargetsType} not supported`}</Typography>
      }
    }
    return (
      <FlexBox vertical ai="start" gap sx={{ mr: "1rem", ml: "1rem", mb: "1rem" }}>
        <FlexBox gap vertical ai="flex-start">
          <Typography color="textPrimary" fontSize=".8rem">
            <Link
              component="button"
              variant="body2"
              fontSize=".8rem"
              fontWeight="bold"
              sx={{ color: theme => theme.palette.primary.main, cursor: "pointer", mr: ".5rem" }}
              onClick={() => {
                const templateUrl = `${location.origin}/csv/${geoTargetsType}-mass-import-template.csv`
                window.open(templateUrl, "_blank")
              }}>
              {translate("campaign.geo-targets.file.import.click.here")}
            </Link>
            {translate("campaign.geo-targets.file.import.help.download")}
          </Typography>
          <FlexBox
            gap=".3rem"
            sx={{
              "& .info-circle-icon": {
                color: theme => theme.palette.primary.main
              }
            }}>
            <FontAwesomeIcon icon={faCircleInfo} size="1x" className="info-circle-icon" />
            <Typography color="textPrimary" fontSize=".8rem">
              {translate("campaign.geo-targets.file.import.help.column")}
            </Typography>
          </FlexBox>
        </FlexBox>
        <FlexBox jc="center" ai="flex-start" fullWidth>
          <GeoTargetsImportFileUpload />
          <FlexBox
            vertical
            gap
            ai="flex-start"
            sx={{ width: "50%", maxWidth: "50%", minWidth: "50%", pl: "1rem", pr: "1rem" }}>
            {files && files.length > 0 ? (
              <FlexBox
                vertical
                fullWidth
                gap
                ai="start"
                sx={{
                  "& .circular-progress": {
                    color: theme => theme.palette.primary.main
                  }
                }}>
                <FlexBox ai="flex-start" vertical fullWidth>
                  <FlexBox fullWidth jc="space-between">
                    <Typography color="textSecondary" fontSize="0.8rem">
                      {translate("label.selected.file")}
                    </Typography>
                    <Typography
                      fontSize=".8rem"
                      sx={{
                        color: theme => theme.palette.text.disabled
                      }}>
                      {getFormattedFileSize(files[0].size)}
                    </Typography>
                  </FlexBox>
                  <Tooltip title={files[0].name}>
                    <Typography
                      fontSize=".8rem"
                      sx={{
                        width: "100%",
                        color: theme => (parsingError ? theme.palette.error.main : theme.palette.text.primary2)
                      }}>
                      {files[0].name}
                    </Typography>
                  </Tooltip>
                </FlexBox>
                {isParsing ? (
                  <CircularProgress className="circular-progress" />
                ) : rows && rows.length > 0 ? (
                  <FlexBox vertical gap=".3rem" fullWidth jc="start" ai="start">
                    <Typography
                      fontSize=".8rem"
                      sx={{
                        width: "100%",
                        color: theme => theme.palette.text.primary2
                      }}>
                      {translate(`campaign.geo-targets.${geoTargetsType}.import.row.count`, rows.length)}
                    </Typography>
                    <FlexBox jc="space-between" fullWidth gap>
                      {isSaving ? (
                        <Typography color="textSecondary" fontSize="0.8rem">
                          {translate("campaign.geo-targets.file.importing")}
                        </Typography>
                      ) : (
                        hasData && (
                          <FlexBox fullWidth vertical>
                            <Divider sx={{ width: "100%", color: theme => theme.palette.text.disabled, mb: "1rem" }} />
                            <RadioGroup
                              value={importType}
                              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                setGeoTargetsFileImportType((event.target as HTMLInputElement).value as any)
                              }
                              sx={{
                                "& .MuiFormControlLabel-label": { fontSize: ".8rem" },
                                "& .MuiButtonBase-root.MuiRadio-root": {
                                  pt: ".2rem",
                                  pb: ".2rem"
                                }
                              }}>
                              <FormControlLabel
                                value="replace"
                                control={<Radio />}
                                label={translate("campaign.geo-targets.file.import.type.replace")}
                              />
                              <FormControlLabel
                                value="insert"
                                control={<Radio />}
                                label={translate("campaign.geo-targets.file.import.type.insert")}
                              />
                            </RadioGroup>
                          </FlexBox>
                        )
                      )}
                    </FlexBox>
                  </FlexBox>
                ) : null}
              </FlexBox>
            ) : (
              <Typography color="textSecondary" fontSize=".8rem" fontStyle="italic">
                {translate("label.no.selected.file")}
              </Typography>
            )}
          </FlexBox>
        </FlexBox>
        {(parsingError || error) && (
          <FlexBox
            vertical
            gap
            sx={{
              p: "1rem",
              width: "100%",
              backgroundColor: theme => alpha(theme.palette.error.light, 0.1),
              borderRadius: ".25rem"
            }}>
            {parsingError && (
              <FlexBox gap sx={{ "& > *": { color: theme => theme.palette.error.main } }}>
                <FontAwesomeIcon icon={faBan} className="icon" />
                <Typography fontSize=".8rem" textAlign="center">
                  {parsingError}
                </Typography>
              </FlexBox>
            )}
            {error && (
              <FlexBox gap sx={{ "& > *": { color: theme => theme.palette.error.main } }}>
                <FontAwesomeIcon icon={faBug} className="icon" />
                <Typography fontSize=".8rem" textAlign="center">
                  {error}
                </Typography>
              </FlexBox>
            )}
          </FlexBox>
        )}
        <FlexBox jc="flex-end" ai="flex-start" fullWidth>
          <Button
            disabled={
              files === null ||
              files.length === 0 ||
              isParsing ||
              isSaving ||
              parsingError !== "" ||
              !rows ||
              rows.length === 0
            }
            startIcon={isSaving ? <CircularProgress size="1rem" /> : undefined}
            variant="contained"
            sx={{ minWidth: "8rem" }}
            onClick={() => importGeoTargetsFile()}>
            <Typography fontWeight="bold" fontSize="small">
              {translate(isSaving ? "campaign.geo-targets.file.importing.button" : "button.import")}
            </Typography>
          </Button>
        </FlexBox>
      </FlexBox>
    )
  }
)

export const GeoTargetsImportPopupContent = connect(
  (state: IRootState): IGeoTargetsImportPopupContentProps => ({
    form: getForm(state),
    fileImport: getGeoTargetsFileImport(state)
  })
)(GeoTargetsImportPopupContentFn)
